import type { ContentKind, CursorPage, FeedItem, NotificationPreferences } from "@dropdex/domain";

export interface DropDexUser {
  userId: string;
  accountId: string | null;
  email: string | null;
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  status: string;
  isAdmin: boolean;
  persistence: "d1" | "mock";
}

export type DropDexOnboardingAlertType =
  | "online-restocks"
  | "in-store-restocks"
  | "smart-watchlist"
  | "releases"
  | "trending"
  | "news";

export interface DropDexOnboardingProfile {
  selectedTcgs: string[];
  alertTypes: DropDexOnboardingAlertType[];
  completed: boolean;
  zipCode: string | null;
  radiusMiles: number;
  nearbyNotificationsEnabled: boolean;
  updatedAt: string | null;
}

export interface DropDexOnboardingInput {
  selectedTcgs: string[];
  alertTypes: DropDexOnboardingAlertType[];
  completed: boolean;
}

export interface SubscriptionSummary {
  plan: {
    name: string;
    amountCents: number;
    currency: string;
    interval: string;
    trialDays: number;
    includesMobile: boolean;
    includesDesktop: boolean;
  };
  subscription: null | {
    id: string;
    status: string;
    currentPeriodEnd: string | null;
    trialEnd: string | null;
    cancelAtPeriodEnd: boolean;
    amountCents: number;
    currency: string;
  };
  entitlements: Array<{ key: string; active: boolean; endsAt: string | null }>;
  isEntitled: boolean;
  effectiveAccess: boolean;
  enforcementEnabled: boolean;
  billingConfigured: boolean;
}

export interface PublicAppConfig {
  supportedTcgs: ReadonlyArray<{ id: string; name: string; shortName: string }>;
  mobileNavigation: ReadonlyArray<string>;
  desktopNavigation: ReadonlyArray<string>;
  socialLinks: ReadonlyArray<{ label: string; href: string }>;
  featureFlags: Record<string, boolean>;
  deepLinks: Record<string, string>;
  accountRoutes: ReadonlyArray<string>;
}

export type DropDexDevicePlatform = "ios" | "android" | "macos" | "windows" | "linux" | "mobile-web" | "desktop-web";

export interface DropDexDevice {
  id: string;
  installationId: string;
  platform: DropDexDevicePlatform;
  deviceName: string | null;
  appVersion: string | null;
  provider: string | null;
  pushEnabled: boolean;
  lastSeenAt: string | null;
  createdAt: string;
}

export interface RegisterDropDexDeviceInput {
  installationId: string;
  platform: DropDexDevicePlatform;
  deviceName?: string;
  appVersion?: string;
}


export interface DropDexChatMessage {
  id: string; userId: string; displayName: string | null; username: string | null; avatarUrl: string | null;
  body: string; imageKey: string | null; imageUrl: string | null; replyToId: string | null;
  createdAt: string; updatedAt: string; pinned: boolean; deleted: boolean;
  reactions: Record<string, number>; reactedByMe: number;
}
export interface DropDexChatPage { items: DropDexChatMessage[]; pageInfo: { endCursor: string | null; hasNextPage: boolean }; unreadCount: number; onlineCount: number; }


export interface InStoreRestockPreference { zipCode:string; radiusMiles:number; notificationsEnabled:boolean; updatedAt?:string; }
export interface InStoreRestockEvent {
  id:string; providerEventId:string; retailer:string; storeId:string; storeName:string|null; storeAddress:string|null; storeCity:string|null; storeState:string|null; storeZipCode:string|null;
  latitude:number; longitude:number; tcgId:string; productId:string|null; productName:string; productImageUrl:string|null; productUrl:string|null; priceCents:number|null; quantity:number|null; status:string; eventTime:string; distanceMiles:number;
}
export interface InStoreRestockPage { preference:InStoreRestockPreference|null; items:InStoreRestockEvent[]; pageInfo:{endCursor:string|null;hasNextPage:boolean}; }

export interface SmartWatchlistItem {
  id:string; productKey:string; productId:string|null; productName:string; tcgId:string;
  productImageUrl:string|null; productUrl:string|null; notifyWhenAvailable:boolean;
  maxPriceCents:number|null; retailers:string[]; pushEnabled:boolean; createdAt:string; updatedAt:string;
}
export interface SmartWatchlistInput {
  productId?:string|null; productName:string; tcgId:string; productImageUrl?:string|null; productUrl?:string|null;
  notifyWhenAvailable?:boolean; maxPriceCents?:number|null; retailers?:string[]; pushEnabled?:boolean;
}

export interface DropDexTrendingProduct {
  productKey:string; productId:string|null; productName:string; tcgId:string;
  productImageUrl:string|null; productUrl:string|null; dexScore:number; demandLabel:string;
  watchlistCount:number; restocks7d:number; activityChangePercent:number|null; clicks7d:number;
  portfolioAdds30d:number; latestPriceCents:number|null; priceChangePercent:number|null;
  retailers:string[]; latestStatus:string|null; latestActivityAt:string;
}
export interface DropDexTrendingPage { items:DropDexTrendingProduct[]; calculatedAt:string; }
export interface DropDexProductActivityInput {
  activityType:"click"|"portfolio-add"; productId?:string|null; productName:string; tcgId:string;
  productImageUrl?:string|null; productUrl?:string|null; source?:string;
}



export interface TcgRelease {
  id:string;slug:string;productKey:string;productId:string|null;productName:string;tcgId:string;
  releaseDate:string;msrpCents:number|null;currency:string;setName:string|null;setInformation:string|null;
  cardCount:number|null;productImageUrls:string[];retailers:string[];preorderStatus:string;productUrl:string|null;
  watchlistCount?:number;restockActivity?:number;dexScore?:number;demandLabel?:string;
}
export interface TcgReleasePage { month:string;items:TcgRelease[]; }

export type PortfolioItemType = "raw-card" | "sealed" | "graded";
export type PortfolioPriceConfidence = "high" | "medium" | "low";
export interface PortfolioCatalogItem {
  providerProductId:string;providerVariantId:string;productName:string;tcgId:string;setName:string|null;cardNumber:string|null;
  rarity:string|null;imageUrl:string|null;productUrl:string|null;itemType:PortfolioItemType;condition:string|null;printing:string|null;
  language:string|null;gradingCompany:string|null;grade:string|null;sealedFormat:string|null;marketPriceCents:number|null;
  currency:string;providerUpdatedAt:string|null;
}
export interface PortfolioCatalogSearch {
  items:PortfolioCatalogItem[];providerConfigured:boolean;gradedEnabled:boolean;
}
export interface PortfolioItem {
  id:string;itemType:PortfolioItemType;tcgId:string;provider:string;providerProductId:string|null;providerVariantId:string|null;
  productName:string;setName:string|null;cardNumber:string|null;rarity:string|null;imageUrl:string|null;productUrl:string|null;
  quantity:number;purchasePriceCents:number|null;purchaseDate:string|null;manualValueCents:number|null;condition:string|null;
  printing:string|null;language:string|null;gradingCompany:string|null;grade:string|null;certificationNumber:string|null;
  sealedFormat:string|null;sealedCondition:string|null;notes:string|null;createdAt:string;updatedAt:string;
  marketPriceCents:number|null;currency:string|null;priceConfidence:PortfolioPriceConfidence|null;providerUpdatedAt:string|null;
  priceObservedAt:string|null;pendingPriceCents:number|null;price7dCents:number|null;currentValueCents:number|null;
  costBasisCents:number|null;gainLossCents:number|null;change7dPercent:number|null;valuationSource:string|null;
  awaitingConfirmation:boolean;
}
export interface PortfolioItemInput {
  itemType:PortfolioItemType;tcgId:string;productName:string;providerProductId?:string|null;providerVariantId?:string|null;
  setName?:string|null;cardNumber?:string|null;rarity?:string|null;imageUrl?:string|null;productUrl?:string|null;
  quantity?:number;purchasePriceCents?:number|null;purchaseDate?:string|null;manualValueCents?:number|null;
  condition?:string|null;printing?:string|null;language?:string|null;gradingCompany?:string|null;grade?:string|null;
  certificationNumber?:string|null;sealedFormat?:string|null;sealedCondition?:string|null;notes?:string|null;
  initialMarketPriceCents?:number|null;providerUpdatedAt?:string|null;
}
export interface DropDexPortfolioPage {
  items:PortfolioItem[];
  summary:{itemCount:number;unitCount:number;currentValueCents:number;costBasisCents:number;gainLossCents:number;unpricedCount:number};
  history:Array<{date:string;valueCents:number}>;
  provider:{name:string;configured:boolean;gradedEnabled:boolean};
}

export interface DiscordConnection {
  configured: boolean;
  connected: boolean;
  accessGranted: boolean;
  discordUser: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
  linkedAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface DropDexClientOptions {
  apiUrl: string;
  getToken: () => Promise<string | null>;
  fetch?: typeof globalThis.fetch;
}

interface ApiEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
}

export class DropDexApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;

  constructor(message: string, options: { code: string; status: number; requestId?: string }) {
    super(message);
    this.name = "DropDexApiError";
    this.code = options.code;
    this.status = options.status;
    this.requestId = options.requestId;
  }
}

function cleanApiUrl(value: string) {
  const apiUrl = value.trim().replace(/\/$/, "");
  if (!apiUrl) throw new Error("DropDex API URL is required.");
  return apiUrl;
}

export class DropDexAppClient {
  private readonly apiUrl: string;
  private readonly getToken: DropDexClientOptions["getToken"];
  private readonly fetcher: typeof globalThis.fetch;

  constructor(options: DropDexClientOptions) {
    this.apiUrl = cleanApiUrl(options.apiUrl);
    this.getToken = options.getToken;
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  private async request<T>(path: string, init: RequestInit = {}, authenticated = true): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");

    if (authenticated) {
      const token = await this.getToken();
      if (!token) {
        throw new DropDexApiError("A DropDex account session is required.", {
          code: "AUTH_TOKEN_MISSING",
          status: 401
        });
      }
      headers.set("authorization", `Bearer ${token}`);
    }

    const response = await this.fetcher(`${this.apiUrl}${path}`, { ...init, headers });
    if (response.status === 204) return null as T;
    const payload = await response.json().catch(() => null) as ApiEnvelope<T> | ApiErrorEnvelope | null;

    if (!response.ok) {
      const apiError = payload && "error" in payload ? payload.error : undefined;
      throw new DropDexApiError(apiError?.message || `DropDex API request failed with status ${response.status}.`, {
        code: apiError?.code || "API_REQUEST_FAILED",
        status: response.status,
        requestId: apiError?.requestId || response.headers.get("x-request-id") || undefined
      });
    }

    if (!payload || !("data" in payload)) {
      throw new DropDexApiError("The DropDex API returned an invalid response.", {
        code: "INVALID_API_RESPONSE",
        status: response.status,
        requestId: response.headers.get("x-request-id") || undefined
      });
    }

    return payload.data;
  }

  getConfig() {
    return this.request<PublicAppConfig>("/api/v1/config", {}, false);
  }

  getCurrentUser() {
    return this.request<DropDexUser>("/api/v1/me");
  }

  getSubscription() {
    return this.request<SubscriptionSummary>("/api/v1/billing/subscription");
  }

  getContent(kind: ContentKind, cursor?: string | null) {
    const query = new URLSearchParams({ kind });
    if (cursor) query.set("cursor", cursor);
    return this.request<CursorPage<FeedItem>>(`/api/v1/content?${query.toString()}`);
  }


  getInStoreRestocks(cursor?:string|null) { const query=new URLSearchParams();if(cursor)query.set("cursor",cursor);return this.request<InStoreRestockPage>("/api/v1/in-store-restocks"+(query.size?`?${query}`:"")); }
  getInStoreRestockPreference() { return this.request<InStoreRestockPreference|null>("/api/v1/in-store-restocks/preferences"); }
  updateInStoreRestockPreference(input:{zipCode:string;notificationsEnabled:boolean;radiusMiles?:number}) { return this.request<InStoreRestockPreference>("/api/v1/in-store-restocks/preferences",{method:"PUT",headers:{"content-type":"application/json"},body:JSON.stringify(input)}); }

  getSmartWatchlist() { return this.request<SmartWatchlistItem[]>("/api/v1/watchlist"); }
  addSmartWatchlistItem(input:SmartWatchlistInput) { return this.request<SmartWatchlistItem>("/api/v1/watchlist",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify(input)}); }
  updateSmartWatchlistItem(itemId:string,input:Partial<SmartWatchlistInput>) { return this.request<SmartWatchlistItem>(`/api/v1/watchlist/${encodeURIComponent(itemId)}`,{method:"PUT",headers:{"content-type":"application/json"},body:JSON.stringify(input)}); }
  deleteSmartWatchlistItem(itemId:string) { return this.request<null>(`/api/v1/watchlist/${encodeURIComponent(itemId)}`,{method:"DELETE"}); }

  getDropDexTrending(limit=12) { return this.request<DropDexTrendingPage>(`/api/v1/trending?limit=${Math.min(25,Math.max(1,Math.round(limit)))}`); }
  getTcgReleases(month:string) { return this.request<TcgReleasePage>(`/api/v1/releases?month=${encodeURIComponent(month)}`); }
  getTcgRelease(slug:string) { return this.request<TcgRelease>(`/api/v1/releases/${encodeURIComponent(slug)}`); }
  trackProductActivity(input:DropDexProductActivityInput) { return this.request<{recorded:boolean}>("/api/v1/trending/activity",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify(input),keepalive:true}); }

  searchPortfolioCatalog(input:{query:string;tcgId:string;itemType:PortfolioItemType}) {
    const query=new URLSearchParams({q:input.query,tcg:input.tcgId,itemType:input.itemType});
    return this.request<PortfolioCatalogSearch>(`/api/v1/catalog/search?${query}`);
  }
  getPortfolio() { return this.request<DropDexPortfolioPage>("/api/v1/portfolio"); }
  addPortfolioItem(input:PortfolioItemInput) { return this.request<PortfolioItem>("/api/v1/portfolio",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify(input)}); }
  updatePortfolioItem(itemId:string,input:Partial<PortfolioItemInput>) { return this.request<PortfolioItem>(`/api/v1/portfolio/${encodeURIComponent(itemId)}`,{method:"PUT",headers:{"content-type":"application/json"},body:JSON.stringify(input)}); }
  deletePortfolioItem(itemId:string) { return this.request<null>(`/api/v1/portfolio/${encodeURIComponent(itemId)}`,{method:"DELETE"}); }
  refreshPortfolioPrices() { return this.request<{queued:number}>("/api/v1/portfolio/refresh",{method:"POST"}); }

  getDiscordConnection() {
    return this.request<DiscordConnection>("/api/v1/discord");
  }

  beginDiscordConnection() {
    return this.request<{ authorizationUrl: string }>("/api/v1/discord/connect", { method: "POST" });
  }

  syncDiscordConnection() {
    return this.request<DiscordConnection>("/api/v1/discord/sync", { method: "POST" });
  }

  disconnectDiscord() {
    return this.request<null>("/api/v1/discord", { method: "DELETE" });
  }

  getOnboarding() {
    return this.request<DropDexOnboardingProfile>("/api/v1/onboarding");
  }

  updateOnboarding(input: DropDexOnboardingInput) {
    return this.request<DropDexOnboardingProfile>("/api/v1/onboarding", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  }

  getNotificationPreferences() {
    return this.request<NotificationPreferences>("/api/v1/notification-preferences");
  }

  updateNotificationPreferences(preferences: NotificationPreferences) {
    return this.request<NotificationPreferences>("/api/v1/notification-preferences", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(preferences)
    });
  }

  registerDevice(input: RegisterDropDexDeviceInput) {
    return this.request<DropDexDevice>("/api/v1/devices", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  }

  getDevices() {
    return this.request<DropDexDevice[]>("/api/v1/devices");
  }

  getChatMessages(cursor?: string | null, limit = 40) {
    const query = new URLSearchParams({ limit: String(limit) }); if (cursor) query.set("cursor", cursor);
    return this.request<DropDexChatPage>(`/api/v1/chat/messages?${query}`);
  }
  sendChatMessage(input: { body: string; replyToId?: string | null; imageKey?: string | null }) {
    return this.request<DropDexChatMessage>("/api/v1/chat/messages", { method:"POST", headers:{"content-type":"application/json"}, body:JSON.stringify(input) });
  }
  uploadChatImage(file: File) {
    const body = new FormData(); body.set("file", file);
    return this.request<{ imageKey:string; imageUrl:string }>("/api/v1/chat/upload", { method:"POST", body });
  }
  reactToChatMessage(messageId:string, emoji:string, active=true) {
    return this.request<{messageId:string;emoji:string;active:boolean}>(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/reactions`, { method:active?"PUT":"DELETE", headers:{"content-type":"application/json"}, body:JSON.stringify({emoji}) });
  }
  markChatRead(messageId:string|null) { return this.request<{read:boolean}>("/api/v1/chat/read", { method:"PUT", headers:{"content-type":"application/json"}, body:JSON.stringify({messageId}) }); }
  updateChatPresence(clientId:string) { return this.request<{online:boolean}>("/api/v1/chat/presence", { method:"PUT", headers:{"content-type":"application/json"}, body:JSON.stringify({clientId}) }); }
  reportChatMessage(messageId:string, reason:string, details?:string) { return this.request(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/report`, { method:"POST", headers:{"content-type":"application/json"}, body:JSON.stringify({reason,details}) }); }
  blockChatUser(userId:string, blocked=true) { return this.request(`/api/v1/chat/users/${encodeURIComponent(userId)}/block`, { method:blocked?"PUT":"DELETE" }); }
  deleteChatMessage(messageId:string) { return this.request(`/api/v1/chat/messages/${encodeURIComponent(messageId)}`, { method:"DELETE" }); }
  pinChatMessage(messageId:string, pinned=true) { return this.request(`/api/v1/chat/messages/${encodeURIComponent(messageId)}/pin`, { method:pinned?"PUT":"DELETE" }); }

  revokeDevice(deviceId: string) {
    return this.request<null>(`/api/v1/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" });
  }
}

export function createDropDexAppClient(options: DropDexClientOptions) {
  return new DropDexAppClient(options);
}
